import { InputGroup } from '@chakra-ui/react'
import { Flex, Icon, IconButton, Input } from '@chakra-ui/react'
import { PhoneOutgoing } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  DIAL_TERMINATOR,
  toDialTarget,
  toMaskedPhoneNumber,
} from '@mangovoice/core'

import {
  TransferHeaderVariants,
  useTransferHeaderStyle,
} from './componentStyles/TransferHeader.style'

const MIN_TARGET_LENGTH = 3

interface TransferHeaderProps {
  transferTarget: string
  onTransferTargetChange: (target: string) => void
  onTransfer: (target: string) => void
}
export const TransferHeader = ({
  transferTarget,
  onTransferTargetChange,
  onTransfer,
}: TransferHeaderProps) => {
  const { t } = useTranslation()
  const dialTarget = toDialTarget(transferTarget)
  const isTransferAvailable = dialTarget.length >= MIN_TARGET_LENGTH
  const variant = isTransferAvailable
    ? TransferHeaderVariants.Default
    : TransferHeaderVariants.Disabled

  const handleTransferTargetChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onTransferTargetChange(toMaskedPhoneNumber(e.target.value))
  }
  const handleTransfer = () => {
    if (!isTransferAvailable) {
      return
    }
    onTransfer(dialTarget)
  }

  const styles = useTransferHeaderStyle({ variant })

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    flexDirection: styles.container?.flexDirection,
  }

  return (
    <Flex css={styles.container} {...containerProps}>
      <InputGroup
        endElement={
          <IconButton
            css={styles.iconButton}
            onClick={handleTransfer}
            aria-label={t('transferHeader.ariaLabel')}
          >
            <Icon as={PhoneOutgoing} css={styles.icon} />
          </IconButton>
        }
      >
        <Input
          css={styles.input}
          variant="outline"
          aria-label={t('transferHeader.inputLabel')}
          placeholder={t('transferHeader.placeholder')}
          value={transferTarget}
          onChange={handleTransferTargetChange}
          onKeyDown={(e) => {
            // `#` means "done, send it" on a desk phone rather than being part
            // of the target, and users press it here out of the same habit.
            // Keyed here rather than in onChange so a pasted `#` cannot dial.
            if (e.key === 'Enter' || e.key === DIAL_TERMINATOR) {
              e.preventDefault()
              handleTransfer()
            }
          }}
        />
      </InputGroup>
    </Flex>
  )
}
